'use client'

import Image from 'next/image'
import { Box, GridCol } from '@mantine/core'
import { usesInsetProductImage, isMedicationSkuCategory } from '@/components/Refresh/SearchResults/searchResultsUtils'
import type { Sku } from '@/types/interfaces/sku'
import { appPath } from '@/lib/getPublicAppUrl'

interface SkuRefreshHeroImageProps {
    sku: Sku | null
    skuCategory?: string
    overrideImageSrc?: string | import('next/image').StaticImageData
    overrideImageAlt?: string
}

/** Client hero image — mirrors `SkuHeroImageServer` for use under refresh (client) layout. */
export function SkuRefreshHeroImage({
    sku,
    skuCategory,
    overrideImageSrc,
    overrideImageAlt,
}: SkuRefreshHeroImageProps) {
    if (overrideImageSrc) {
        return (
            <GridCol span={{ base: 12, md: 5, lg: 6 }} py={{ base: 0, md: 8 }}>
                <Box
                    h={{ base: 175, md: 'auto' }}
                    style={{
                        position: 'relative',
                        width: '100%',
                        maxWidth: 600,
                        marginLeft: 0,
                        marginRight: 'auto',
                        aspectRatio: '1 / 1',
                        borderRadius: 16,
                        background: '#EBDACE',
                        overflow: 'hidden',
                    }}
                >
                    <Image
                        src={overrideImageSrc}
                        alt={overrideImageAlt ?? sku?.name ?? 'Product image'}
                        fill
                        sizes="(max-width: 48em) 100vw, 50vw"
                        style={{ objectFit: 'cover', objectPosition: 'center' }}
                        priority
                    />
                </Box>
            </GridCol>
        )
    }

    const isLab = skuCategory === 'lab'
    const isLabOrMedication = usesInsetProductImage(skuCategory)

    const fallbackImage = isMedicationSkuCategory(skuCategory)
        ? appPath('/self-serve/hero-image--medications.jpg')
        : isLab
          ? appPath('/self-serve/hero-image--labs.jpg')
          : appPath('/self-serve/hero-image.png')

    const fallbackImageMobile = isLab ? appPath('/self-serve/hero-image--labs-mobile.png') : fallbackImage

    const usesFallback = !sku?.s3_object_key || sku.s3_object_key.startsWith('fallback-icons/')
    const imageUrl = usesFallback
        ? fallbackImage
        : `https://${process.env.NEXT_PUBLIC_ASSETS_DOMAIN}/${sku.s3_object_key}`

    if (usesFallback && isLab) {
        return (
            <GridCol span={{ base: 12, md: 5, lg: 6 }} py={{ base: 0, md: 8 }}>
                <Box
                    h={{ base: 175, md: 'auto' }}
                    style={{
                        position: 'relative',
                        width: '100%',
                        maxWidth: 600,
                        marginLeft: 0,
                        marginRight: 'auto',
                        aspectRatio: '1 / 1',
                        borderRadius: 16,
                        background: '#EBDACE',
                        overflow: 'hidden',
                    }}
                >
                    <picture>
                        <source media="(max-width: 48em)" srcSet={fallbackImageMobile} />
                        <img
                            src={fallbackImage}
                            alt={sku?.name || 'Product image'}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                objectPosition: 'center',
                            }}
                        />
                    </picture>
                </Box>
            </GridCol>
        )
    }

    return (
        <GridCol span={{ base: 12, md: 5, lg: 6 }} py={{ base: 0, md: 8 }}>
            <Box
                h={{ base: 175, md: 'auto' }}
                style={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: 600,
                    marginLeft: 0,
                    marginRight: 'auto',
                    aspectRatio: '1 / 1',
                    borderRadius: 16,
                    background: '#EBDACE',
                    overflow: 'hidden',
                }}
            >
                <Image
                    src={imageUrl}
                    alt={sku?.name || 'Product image'}
                    fill
                    sizes="(max-width: 48em) 100vw, 50vw"
                    style={{
                        objectFit: isLabOrMedication ? 'contain' : 'cover',
                        objectPosition: 'center',
                    }}
                    priority
                />
            </Box>
        </GridCol>
    )
}
